/**
 * Runtime configuration, fetched from GET /config (served by server.ts) before
 * the app renders. "Build once, deploy anywhere": nothing environment-specific
 * is baked into the bundle.
 *
 * The /config payload shape is shared with frontend/ (both servers emit it).
 */

export type AuthProviderType = "microsoft" | "google" | "dev";

export interface RuntimeConfig {
  authProvider: AuthProviderType;
  azureClientId: string;
  azureTenantId: string;
  /** Must point at the /redirect bridge page (MSAL v5). */
  azureRedirectUri: string;
  apiScope: string;
  apiBaseUrl: string;
  /** Absolute ws(s):// base for WebSockets (voice transcription): WS_API_URL, else derived from apiBaseUrl. */
  wsApiUrl: string;
  /** Application Insights; empty disables error tracking (local dev). */
  appInsightsConnectionString: string;
  mode: string;
  isDev: boolean;
  featureFlags: Record<string, boolean>;
}

let config: RuntimeConfig | null = null;

export function getRuntimeConfig(): RuntimeConfig {
  if (!config) throw new Error("Runtime config not initialized. Call initRuntimeConfig() first.");
  return config;
}

/** Test seam: set config directly instead of fetching /config. */
export function setRuntimeConfig(next: RuntimeConfig | null): void {
  config = next;
}

function toWebSocketUrl(url: string, origin: string): string {
  return new URL(url, origin).toString().replace(/^http/, "ws").replace(/\/$/, "");
}

export function parseRuntimeConfig(data: Record<string, unknown>, origin: string): RuntimeConfig {
  const str = (key: string, fallback = "") =>
    typeof data[key] === "string" && data[key] ? String(data[key]) : fallback;
  const mode = str("mode", "production");
  const rawFlags = (data.featureFlags ?? {}) as Record<string, unknown>;
  const apiBaseUrl = str("apiBaseUrl", "/api/v1").replace(/\/$/, "");
  return {
    authProvider: str("authProvider", "microsoft") as AuthProviderType,
    azureClientId: str("azureClientId"),
    azureTenantId: str("azureTenantId"),
    azureRedirectUri: str("azureRedirectUri", `${origin}/redirect`),
    apiScope: str("apiScope"),
    apiBaseUrl,
    wsApiUrl: toWebSocketUrl(str("wsApiUrl", apiBaseUrl), origin),
    appInsightsConnectionString: str("appInsightsConnectionString"),
    mode,
    isDev: mode === "development",
    featureFlags: Object.fromEntries(Object.entries(rawFlags).map(([k, v]) => [k, v === true])),
  };
}

export async function initRuntimeConfig(): Promise<RuntimeConfig> {
  if (config) return config;
  const response = await fetch("/config");
  if (!response.ok) throw new Error(`Failed to fetch runtime config: ${response.status}`);
  config = parseRuntimeConfig(await response.json(), globalThis.location.origin);
  return config;
}
